import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import SearchIcon from "@mui/icons-material/Search";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import {
	Alert,
	Chip,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Snackbar,
	Stack,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import React, { useState } from "react";
import { CelTask, cancelRunningCeleryTask } from "../../api/celtask";
import { getTaskDisplayInfo } from "../../utils/celery-task-display";

const TaskIcon = ({ taskName }: { taskName: string }) => {
	const info = getTaskDisplayInfo(taskName);
	const iconStyle = { fontSize: 16 };
	switch (info.icon) {
		case "Search":
			return <SearchIcon sx={iconStyle} />;
		case "AutoAwesome":
			return <AutoAwesomeIcon sx={iconStyle} />;
		default:
			return <HourglassBottomIcon sx={iconStyle} />;
	}
};

/**
 * "Updating" chip listing the celery tasks running for an assignment.
 * Admins can click it to cancel a running task.
 */
export const RunningTasksChip = ({
	tasks,
	canCancel,
	onCancelled,
}: {
	tasks: CelTask[];
	canCancel: boolean;
	onCancelled: () => void;
}) => {
	const theme = useTheme();
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const [cancellingTaskId, setCancellingTaskId] = useState<string | null>(null);
	const [error, setError] = useState<string>("");

	const handleCancel = (taskId: string) => {
		setCancellingTaskId(taskId);
		cancelRunningCeleryTask(taskId)
			.then(() => {
				setAnchorEl(null);
				onCancelled();
			})
			.catch((e: Error) => setError(e.message))
			.finally(() => setCancellingTaskId(null));
	};

	return (
		<>
			<Tooltip
				data-component="celery-tooltip"
				title={
					<Stack spacing={1} data-component="celery-stack">
						{tasks.map((task) => (
							<Stack
								key={task.task_id}
								direction="row"
								spacing={1}
								alignItems="center"
								data-component="celery-task"
							>
								<TaskIcon taskName={task.task_name} />
								<Typography variant="body2" data-component="task-label">
									{getTaskDisplayInfo(task.task_name).label}
								</Typography>
							</Stack>
						))}
						{canCancel && (
							<Typography variant="caption" data-component="cancel-hint">
								Click to cancel
							</Typography>
						)}
					</Stack>
				}
			>
				<Chip
					data-component="updating-chip"
					icon={
						<HourglassBottomIcon style={{ color: theme.status.updating }} />
					}
					label="Updating"
					size="small"
					onClick={
						canCancel
							? (event: React.MouseEvent<HTMLElement>) =>
									setAnchorEl(event.currentTarget)
							: undefined
					}
					sx={{
						backgroundColor: theme.status.info,
						color: "common.black",
					}}
				/>
			</Tooltip>
			<Menu
				data-component="running-tasks-menu"
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={() => setAnchorEl(null)}
			>
				{tasks.map((task) => (
					<MenuItem
						key={task.task_id}
						data-component="cancel-task-item"
						disabled={cancellingTaskId !== null}
						onClick={() => handleCancel(task.task_id)}
					>
						<ListItemIcon>
							<StopCircleIcon fontSize="small" color="error" />
						</ListItemIcon>
						<ListItemText
							primary={`Cancel ${getTaskDisplayInfo(
								task.task_name
							).label.toLowerCase()}`}
						/>
					</MenuItem>
				))}
			</Menu>
			<Snackbar
				data-component="cancel-task-error"
				open={error !== ""}
				autoHideDuration={6000}
				onClose={() => setError("")}
			>
				<Alert severity="error" onClose={() => setError("")}>
					{error}
				</Alert>
			</Snackbar>
		</>
	);
};
